import type { Feature, Geometry, Position } from "geojson";

/**
 * 시도(GeoJSON) → 우리 7권역 매핑.
 *
 * GeoJSON 은 행정 시도 17개인데 서비스의 지역 체계는 7권역이다(#185). 경기·인천처럼
 * 합쳐지는 곳이 있어 1:1 이 아니므로 여기서 접어준다.
 */
const SIDO_TO_REGION: Record<string, string> = {
  "11": "SEOUL",
  "41": "GYEONGGI_INCHEON",
  "28": "GYEONGGI_INCHEON",
  "51": "GANGWON",
  "42": "GANGWON", // 개편 전 코드가 섞여 들어올 수 있다
  "43": "CHUNGCHEONG",
  "44": "CHUNGCHEONG",
  "30": "CHUNGCHEONG",
  "36": "CHUNGCHEONG",
  "46": "JEOLLA",
  "52": "JEOLLA",
  "45": "JEOLLA", // 전북 개편 전 코드
  "29": "JEOLLA",
  "47": "GYEONGSANG",
  "48": "GYEONGSANG",
  "26": "GYEONGSANG",
  "27": "GYEONGSANG",
  "31": "GYEONGSANG",
  "50": "JEJU",
};

function eachPosition(geom: Geometry, fn: (p: Position) => void): void {
  if (geom.type === "Polygon") geom.coordinates.forEach((ring) => ring.forEach(fn));
  else if (geom.type === "MultiPolygon")
    geom.coordinates.forEach((poly) => poly.forEach((ring) => ring.forEach(fn)));
}

/**
 * 권역별 대표 좌표 — 그 권역에 속한 시도 폴리곤 전체의 **바운딩박스 중심**.
 *
 * 무게중심(면적 가중) 대신 bbox 중심을 쓰는 이유는 해안선이 복잡한 권역에서
 * 무게중심이 육지 밖으로 밀려나기 때문이다. bbox 중심은 거칠지만 항상 권역 안쪽에 든다.
 */
export function regionAnchors(
  features: Feature<Geometry>[]
): Record<string, { lat: number; lon: number }> {
  const box: Record<string, { minLat: number; maxLat: number; minLon: number; maxLon: number }> =
    {};

  features.forEach((f) => {
    const sido = String((f.properties as { sido?: string } | null)?.sido ?? "");
    const region = SIDO_TO_REGION[sido];
    if (!region || !f.geometry) return;
    eachPosition(f.geometry, ([lon, lat]) => {
      if (lon == null || lat == null) return;
      const b = (box[region] ??= { minLat: lat, maxLat: lat, minLon: lon, maxLon: lon });
      if (lat < b.minLat) b.minLat = lat;
      if (lat > b.maxLat) b.maxLat = lat;
      if (lon < b.minLon) b.minLon = lon;
      if (lon > b.maxLon) b.maxLon = lon;
    });
  });

  return Object.fromEntries(
    Object.entries(box).map(([region, b]) => [
      region,
      { lat: (b.minLat + b.maxLat) / 2, lon: (b.minLon + b.maxLon) / 2 },
    ])
  );
}
